import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  calculatePension,
  type CalculationContext,
} from "../../calculation/elakeArvio";
import { calculateKelaPension } from "../../calculation/KelaCalculation";
import {
  type AccruedPensions,
  accruedPensionsValue,
  type IndexData,
  type PensionEstimate,
  pensionEstimateValue,
  type YearConstants,
} from "../../types/Codes";
import type {
  ElakeArvioModel,
  PensionCalculationResult,
} from "../../types/ElakeArvioModel";
import type {
  KelaModel,
  KelaPensionCalculationResult,
} from "../../types/KelaModel";
import {
  type AgeResult,
  calculateTargetDateForNextMonth,
} from "../../utils/ageUtils";
import { rangeClosed } from "../../utils/array-utils";
import { getBirthYearOptions } from "../../utils/birthYearUtils";
import { getMonthName, scrollTo } from "../../utils/form-util";
import { localeEuroSumOrZero } from "../../utils/numberUtils";
import { trackEvent } from "../../utils/piwik-util";
import { parseQuery } from "../../utils/queryUtils";
import CalculationResult from "../CalculationResult";
import CodesContext from "../CodesContext";
import HelpTextAccordion from "../HelpTextAccordion";
import LargeSelectInput from "../LargeSelectInput";
import NumberInput from "../NumberInput";
import RadioInput from "../RadioInput";
import ResultsGroup from "../ResultsGroup";
import { useCodeList } from "../useCodeList";
import { useValidation } from "../useValidation";
import { useValidationOnLanguageChange } from "../useValidationOnLanguageChange";
import ValidationContext, { type Validation } from "../ValidationContext";
import ValidationFeedback from "../ValidationFeedback";

const ElakeArvioCalculator: React.FC = () => {
  const yearConstants = useCodeList<YearConstants>("year-constants");
  const pensionEstimate = useCodeList<PensionEstimate>("pension-estimate");
  const index = useCodeList<IndexData>("index");
  const accruedPensions = useCodeList<AccruedPensions>("accrued-pensions");
  return (
    <CodesContext.Provider
      value={{ yearConstants, pensionEstimate, index, accruedPensions }}
    >
      <ElakeArvioView />
    </CodesContext.Provider>
  );
};

const ElakeArvioView: React.FC = () => {
  type Age = { years: number; months: number };

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { yearConstants, pensionEstimate, index } = useContext(CodesContext);
  const [model, setModel] = useState<ElakeArvioModel>({
    accruedPension: 0,
    birthMonth: 0,
    birthYear: 0,
    income: 0,
    incomeDevelopment: "projected",
    pensionAgeMonth: 0,
    pensionAgeYear: 0,
    priceConversion: true,
  });
  const resultsSection = useRef<HTMLDivElement | null>(null);
  const { search } = useLocation();
  const query = parseQuery(search);
  // Allow specifying year for e.g. repeatable Robot testing and manual testing:
  const year = Number.parseInt(query.year) || new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const validation = useValidation();

  const [calcResult, setCalcResult] = useState<PensionCalculationResult | null>(
    null,
  );
  const [kelaResult, setKelaResult] =
    useState<KelaPensionCalculationResult | null>(null);
  const [isInRelationship, setIsInRelationship] = useState<boolean>(true); // Affects Kela pension

  const calculationContext: CalculationContext | null = useMemo(() => {
    if (model.birthYear === 0) {
      return null;
    }
    return {
      calculationYear: year,
      calculationMonth: currentMonth, //0-based
      lowestPensionAgeYear:
        pensionEstimateValue(model.birthYear, "alvev", pensionEstimate) ?? 0,
      lowestPensionAgeMonth:
        pensionEstimateValue(model.birthYear, "alvekk", pensionEstimate) ?? 0,
      lifetimeFactor:
        pensionEstimateValue(model.birthYear, "ek", pensionEstimate) ?? 0,
      indexData: index,
      yearConstants,
    };
  }, [
    yearConstants,
    index,
    pensionEstimate,
    model.birthYear,
    currentMonth,
    year,
  ]);

  const currentAge = useMemo((): Age | null => {
    if (!calculationContext?.calculationYear) return null;
    let years = calculationContext.calculationYear - model.birthYear;
    let months = currentMonth + 1 - model.birthMonth;

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return { years, months };
  }, [
    calculationContext?.calculationYear,
    model.birthYear,
    model.birthMonth,
    currentMonth,
  ]);

  // This is only helper, returning years and months
  const lowestPensionAge: Age | null = useMemo(() => {
    if (!calculationContext?.lowestPensionAgeYear) return null;
    return {
      years: calculationContext.lowestPensionAgeYear,
      months: calculationContext.lowestPensionAgeMonth,
    };
  }, [
    calculationContext?.lowestPensionAgeYear,
    calculationContext?.lowestPensionAgeMonth,
  ]);

  const compareAges = (age1: Age, age2: Age): number =>
    age1.years * 12 + age1.months - (age2.years * 12 + age2.months) || 0;

  const getKelaModel = (
    pensionCalculationResult: PensionCalculationResult,
    pensionModel: ElakeArvioModel,
    inRelationship: boolean,
  ): KelaModel => {
    return {
      earningsRelatedPension:
        pensionCalculationResult?.pensionWithoutIncreaseForLateRetirement,
      earningsRelatedPensionAndIncreaseForLateRetirement:
        pensionCalculationResult.pension,
      inRelationship,
      birthYear: pensionModel.birthYear,
      pensionAgeYear: pensionModel.pensionAgeYear,
      pensionAgeMonth: pensionModel.pensionAgeMonth,
      priceConversion: pensionModel.priceConversion,
      priceLevelIncrease: pensionCalculationResult?.priceLevelIncrease,
    };
  };

  // Price level options are displayed if the persons lowest pension year
  // (the first year he or she could start pension)
  // is the same or lower than the calculation year (current year in normal UI use)
  const showPriceLevelOptions = useMemo(() => {
    if (!lowestPensionAge || !year) {
      return false;
    }
    // What is the date (year and month) when person could start pension
    // Note that pension can be started next month after person turns to lowest pension age
    const lowestPensionDate = calculateTargetDateForNextMonth(
      model.birthYear,
      model.birthMonth,
      lowestPensionAge.years,
      lowestPensionAge.months,
    );
    return lowestPensionDate.year <= year;
  }, [lowestPensionAge, year, model.birthYear, model.birthMonth]);

  useEffect(() => {
    if (!currentAge || !lowestPensionAge || !calculationContext) return;

    if (model.pensionAgeYear === 0) {
      if (compareAges(currentAge, lowestPensionAge) > 0) {
        // Current age is over lowest pension age, use current time as retirement age
        model.pensionAgeYear = currentAge.years;
        model.pensionAgeMonth = currentAge.months;
      } else {
        model.pensionAgeYear = lowestPensionAge.years;
        model.pensionAgeMonth = lowestPensionAge.months;
      }
      setModel(model);
    }

    const result = calculatePension({ ...model }, calculationContext); //Use copy of model, calculatePension modifies it
    setCalcResult(result);
    setKelaResult(null); //Reset Kela result, it may not be displayed after recheck
  }, [currentAge, lowestPensionAge, model, calculationContext]);

  // Calculate Kela pension if pensioner will be at least 55 years at the end of this year
  useEffect(() => {
    if (!calculationContext || !calcResult || !model) return;
    if (year - model.birthYear < 55) return;
    // First, calculate if Kela pension is given when isInRelationShip is false, i.e. living alone
    const kelaModel = getKelaModel(calcResult, model, false);
    const kelaPensionWhenLivingAlone = calculateKelaPension(
      kelaModel,
      calculationContext,
    );
    if (kelaPensionWhenLivingAlone.pension > 0) {
      if (!isInRelationship) {
        setKelaResult(kelaPensionWhenLivingAlone);
      } else {
        kelaModel.inRelationship = true;
        setKelaResult(calculateKelaPension(kelaModel, calculationContext));
      }
    } else {
      setKelaResult(null); //Reset, no Kela pension to display
    }
  }, [isInRelationship, model, calculationContext, calcResult, year]);

  // Move focus to results section when results are calculated (kohta 6)
  useEffect(() => {
    if (calcResult) {
      resultsSection.current?.focus();
      scrollTo(resultsSection);
    }
  }, [calcResult]);

  const retirementYearAndMonth: AgeResult | null = useMemo(() => {
    if (!model.birthYear || !currentAge || !lowestPensionAge) return null;

    return calculateTargetDateForNextMonth(
      model.birthYear,
      model.birthMonth,
      model.pensionAgeYear,
      model.pensionAgeMonth,
    );
  }, [
    model.pensionAgeYear,
    model.pensionAgeMonth,
    model.birthYear,
    model.birthMonth,
    currentAge,
    lowestPensionAge,
    year,
    currentMonth,
  ]);

  const maxAge = 70; //Todo: should this come from data?

  const retirementAgeYearOptions = useMemo(() => {
    if (!currentAge || !lowestPensionAge)
      return rangeClosed(63, maxAge).map((y) => ({ value: y, label: y + "" }));
    if (compareAges(currentAge, lowestPensionAge) > 0) {
      return rangeClosed(currentAge.years, maxAge).map((y) => ({
        value: y,
        label: y + "",
      }));
    } else {
      return rangeClosed(lowestPensionAge.years, maxAge).map((y) => ({
        value: y,
        label: y + "",
      }));
    }
  }, [currentAge, lowestPensionAge]);

  // Return highest month to be disabled. Return -1 if none months should be disabled
  const getHighestMonthToDisable = (
    year: number,
    lowestPensionAge: Age,
  ): number => {
    if (!currentAge || year === maxAge) return -1;
    if (
      compareAges(currentAge, lowestPensionAge) > 0 &&
      year === currentAge.years
    ) {
      return currentAge.months - 1;
    } else if (year === lowestPensionAge.years) {
      return lowestPensionAge.months - 1;
    }
    return -1;
  };

  const precomputedMonthOptions = useMemo(() => {
    if (!retirementAgeYearOptions || !lowestPensionAge) return {};

    const result: Record<
      number,
      { value: number; label: string; isDisabled: boolean }[]
    > = {};

    retirementAgeYearOptions.forEach((yearOption) => {
      const year = yearOption.value;
      const highestMonthDisabled = getHighestMonthToDisable(
        year,
        lowestPensionAge,
      );
      result[year] = Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: i.toString(),
        // With max year, only 0 month is selectable, with other years all months before highestMonthDisabled
        isDisabled: year === maxAge ? i !== 0 : i <= highestMonthDisabled,
      }));
    });
    return result;
  }, [retirementAgeYearOptions, lowestPensionAge]);

  // Select another non-disabled month option, if current month selection is disabled
  useEffect(() => {
    const monthOptionsForYear =
      precomputedMonthOptions[model.pensionAgeYear] ?? []; // Get options for current year

    const firstValidMonth =
      [...monthOptionsForYear].find((opt) => !opt.isDisabled)?.value ?? 0;

    if (
      !monthOptionsForYear.some(
        (opt) => opt.value === model.pensionAgeMonth && !opt.isDisabled,
      )
    ) {
      setModel((prev: ElakeArvioModel) => ({
        ...prev,
        pensionAgeMonth: firstValidMonth,
      }));
    }
  }, [model.pensionAgeYear, model.pensionAgeMonth, precomputedMonthOptions]);

  const amount = (n: number) => localeEuroSumOrZero(language, n);

  return (
    <>
      <Container className="calculator-container content-container">
        <h1>{t("elake-arvio-laskuri.calculatorTitle")}</h1>
        <p className="ingress">{t("elake-arvio-laskuri.ingress1")}</p>
        <p className="ingress">{t("elake-arvio-laskuri.ingress2")}</p>
      </Container>
      <div className="form-container py-4">
        <Container className="content-container">
          <Row>
            <Col md={12}>
              <ElakeArvioForm
                year={year}
                onClear={() => {}}
                onSubmit={(event, model) => {
                  event.preventDefault();
                  setModel(model);
                }}
                validation={validation}
              />
            </Col>
          </Row>
          <Row>
            {!validation.validationFeedback.length && model && (
              <Col
                id="results-region"
                role="region"
                aria-label={t("elake-arvio-laskuri.results.title")}
                md={12}
                className="mt-4"
                ref={resultsSection}
                tabIndex={-1}
              >
                {calcResult && (
                  <div className="results-container">
                    <h2>{t("elake-arvio-laskuri.results.title")}</h2>

                    <Form
                      noValidate
                      onSubmit={() => {}}
                      className="calculator-form"
                      id={"elake-laskuri-result-form"}
                    >
                      {showPriceLevelOptions ? (
                        <ResultsGroup
                          legend={t(
                            "elake-arvio-laskuri.results.priceLevel.label",
                          )}
                          className="mb-2"
                        >
                          <p>
                            {t(
                              "elake-arvio-laskuri.results.priceLevel.explanation",
                            )}
                          </p>
                          <HelpTextAccordion fieldName="elake-arvio-laskuri.results.priceLevel" />
                          <div className="radio-group-wrapper">
                            <RadioInput
                              fieldName="elake-arvio-laskuri.results.priceLevel"
                              ariaDescribedBy="elake-arvio-laskuri.results.priceLevel-help"
                              value={
                                model.priceConversion
                                  ? "currentYear"
                                  : "nominal"
                              }
                              options={[
                                {
                                  value: "currentYear",
                                  label: t(
                                    "elake-arvio-laskuri.results.priceLevel.currentYearLevel",
                                  ),
                                },
                                {
                                  value: "nominal",
                                  label: t(
                                    "elake-arvio-laskuri.results.priceLevel.nominalPriceLevel",
                                  ),
                                },
                              ]}
                              onChange={(value: string) => {
                                if (value === "currentYear") {
                                  setModel((fm) => ({
                                    ...fm,
                                    priceConversion: true,
                                  }));
                                } else if (value === "nominal") {
                                  setModel((fm) => ({
                                    ...fm,
                                    priceConversion: false,
                                  }));
                                }
                              }}
                            />
                          </div>
                        </ResultsGroup>
                      ) : (
                        <p>
                          {t(
                            "elake-arvio-laskuri.results.priceLevelInformation",
                          )}
                        </p>
                      )}

                      {kelaResult && (
                        <ResultsGroup
                          legend={t(
                            "elake-arvio-laskuri.results.kela.livingWithSpouse.label",
                          )}
                        >
                          <HelpTextAccordion
                            fieldName={
                              "elake-arvio-laskuri.results.kela.livingWithSpouse"
                            }
                          />
                          <div className="radio-group-wrapper">
                            <RadioInput
                              fieldName={
                                "elake-arvio-laskuri.results.kela.livingWithSpouse"
                              }
                              ariaDescribedBy="elake-arvio-laskuri.results.kela.livingWithSpouse-help"
                              options={[
                                {
                                  value: "true",
                                  label: t(
                                    "elake-arvio-laskuri.results.kela.livingWithSpouse.withSpouse",
                                  ),
                                },
                                {
                                  value: "false",
                                  label: t(
                                    "elake-arvio-laskuri.results.kela.livingWithSpouse.alone",
                                  ),
                                },
                              ]}
                              value={isInRelationship.toString()} // Convert boolean to string for selection
                              onChange={(value) =>
                                setIsInRelationship(value === "true")
                              } // Convert string to boolean
                            />
                          </div>
                        </ResultsGroup>
                      )}

                      <p>
                        {t(
                          "elake-arvio-laskuri.results.lowestPensionAge.label",
                          {
                            years: lowestPensionAge?.years,
                            months: lowestPensionAge?.months,
                          },
                        )}
                      </p>
                      <HelpTextAccordion
                        fieldName={
                          "elake-arvio-laskuri.results.lowestPensionAge"
                        }
                      />

                      <ResultsGroup
                        legend={t(
                          "elake-arvio-laskuri.results.retirementAge.label",
                        )}
                      >
                        <div className="side-by-side-container">
                          <LargeSelectInput<number>
                            fieldName="elake-arvio-laskuri.results.pensionAge.years"
                            label={
                              <span className="error-label">
                                {t(
                                  "elake-arvio-laskuri.results.pensionAge.years.label",
                                )}
                              </span>
                            }
                            options={retirementAgeYearOptions}
                            value={model.pensionAgeYear}
                            setValue={(selected) => {
                              if (selected?.value !== undefined) {
                                setModel((fm) => ({ ...fm, pensionAgeYear: selected.value }));
                              }
                            }}
                          />
                          <LargeSelectInput<number>
                            fieldName="elake-arvio-laskuri.results.pensionAge.months"
                            key={model.pensionAgeMonth}
                            label={
                              <span className="error-label">
                                {t(
                                  "elake-arvio-laskuri.results.pensionAge.months.label",
                                )}
                              </span>
                            }
                            options={
                              precomputedMonthOptions[model.pensionAgeYear] ||
                              []
                            }
                            value={model.pensionAgeMonth}
                            setValue={(selected) => {
                              if (selected?.value !== undefined) {
                                setModel((fm) => ({ ...fm, pensionAgeMonth: selected.value }));
                              }
                            }}
                          />
                        </div>
                      </ResultsGroup>
                    </Form>

                    <p>
                      {t("elake-arvio-laskuri.results.retirementMonthAndYear", {
                        month:
                          language === "en"
                            ? getMonthName(retirementYearAndMonth?.month, "en")
                            : retirementYearAndMonth?.month,
                        year: retirementYearAndMonth?.year,
                      })}
                    </p>

                    {calcResult.pension !== undefined && (
                      <CalculationResult
                        fieldName="elake-arvio-laskuri.results.pension"
                        labelExtra={
                          !model.priceConversion
                            ? calcResult.pensionYear.toString()
                            : undefined
                        }
                      >
                        <>{amount(calcResult.pension)}</>
                      </CalculationResult>
                    )}

                    {kelaResult && (
                      <>
                        <CalculationResult
                          fieldName="elake-arvio-laskuri.results.kela.pension"
                          labelExtra={
                            !model.priceConversion
                              ? calcResult.pensionYear.toString()
                              : undefined
                          }
                        >
                          <>{amount(kelaResult.pension)}</>
                        </CalculationResult>
                        <CalculationResult
                          fieldName="elake-arvio-laskuri.results.kela.fullPension"
                          labelExtra={
                            !model.priceConversion
                              ? calcResult.pensionYear.toString()
                              : undefined
                          }
                        >
                          <>{amount(kelaResult.pension + calcResult.pension)}</>
                        </CalculationResult>
                      </>
                    )}

                    <div className="result-section">
                      <p className="mb-0 fw-bold">
                        {t("elake-arvio-laskuri.results.lifetimeFactor.label", {
                          lifetimeFactor: calculationContext?.lifetimeFactor
                            .toString()
                            .replace(".", ","),
                        })}
                      </p>
                      <HelpTextAccordion
                        fieldName={"elake-arvio-laskuri.results.lifetimeFactor"}
                      />
                    </div>

                    {calcResult.income !== undefined && (
                      <CalculationResult fieldName="elake-arvio-laskuri.results.income">
                        <>{amount(calcResult.income)}</>
                      </CalculationResult>
                    )}

                    {calcResult.income !== undefined &&
                      calcResult.pension !== undefined && (
                        <CalculationResult
                          className="mb-0"
                          fieldName="elake-arvio-laskuri.results.pensionRelativeToIncome"
                        >
                          <>
                            {amount(
                              ((calcResult.pension +
                                (kelaResult?.pension ?? 0)) /
                                calcResult.income) *
                                100,
                            )}
                          </>
                        </CalculationResult>
                      )}

                    {!model.priceConversion &&
                      calcResult.pensionYear === year && (
                        <div className="mt-4 mb-0">
                          <hr className="grey-divider" aria-hidden="true" />
                          <h2>
                            {t(
                              "elake-arvio-laskuri.results.priceLevel.result.title",
                            )}
                          </h2>

                          {calcResult.pensionNextYear !== undefined && (
                            <CalculationResult
                              fieldName="elake-arvio-laskuri.results.pension"
                              labelExtra={(calcResult.pensionYear + 1).toString()}
                            >
                              <>{amount(calcResult.pensionNextYear)}</>
                            </CalculationResult>
                          )}

                          {kelaResult?.pensionNextYear !== undefined && (
                            <CalculationResult
                              fieldName="elake-arvio-laskuri.results.kela.pension"
                              labelExtra={(calcResult.pensionYear + 1).toString()}
                            >
                              <>{amount(kelaResult.pensionNextYear)}</>
                            </CalculationResult>
                          )}

                          {calcResult.pensionNextYear !== undefined &&
                            kelaResult?.pensionNextYear !== undefined && (
                              <CalculationResult
                                fieldName="elake-arvio-laskuri.results.kela.fullPension"
                                labelExtra={
                                  (calcResult.pensionYear + 1).toString()
                                }
                              >
                                <>
                                  {amount(
                                    calcResult.pensionNextYear +
                                      kelaResult.pensionNextYear,
                                  )}
                                </>
                              </CalculationResult>
                            )}

                          <p>
                            {t(
                              "elake-arvio-laskuri.results.priceLevel.result.explanation",
                            )}
                          </p>
                        </div>
                      )}
                  </div>
                )}
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </>
  );
};

interface FormProps {
  year: number;
  onSubmit: (
    event: React.SyntheticEvent<HTMLFormElement>,
    model: ElakeArvioModel,
  ) => void;
  onClear: () => void;
  validation: Validation;
}

const ElakeArvioForm: React.FC<FormProps> = ({
  year,
  onSubmit,
  onClear,
  validation,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const topSection = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const errorSummary = useRef<HTMLDivElement>(null);
  useValidationOnLanguageChange(
    formRef,
    validation.validateForm,
    validation.validated,
  );

  const [formModel, setFormModel] = useState<ElakeArvioModel>({
    accruedPension: 0,
    birthMonth: 0,
    birthYear: 0,
    income: 0,
    incomeDevelopment: "projected",
    pensionAgeMonth: 0,
    pensionAgeYear: 0,
    priceConversion: true,
  });

  const { accruedPensions } = useContext(CodesContext);
  const medianAccruedPension = useMemo(() => {
    if (!accruedPensions || !formModel.birthYear) {
      return undefined;
    }
    const { birthYear } = formModel;
    const accrued = accruedPensionsValue(
      formModel.birthYear,
      "all",
      accruedPensions,
    );
    return accrued
      ? t("elake-arvio-laskuri.medianAccruedPension", { birthYear, accrued })
      : undefined;
  }, [accruedPensions, formModel.birthYear]);

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    const validateResult = validation.validate(event);
    if (validateResult) {
      // Reset these after "Calculate is clicked"
      formModel.pensionAgeYear = 0;
      formModel.pensionAgeMonth = 0;
      formModel.priceConversion = true;
      onSubmit(event, formModel);
      // Move focus to results region after calculations
      const resultsRegion = document.getElementById("results-region");
      if (resultsRegion) {
        resultsRegion.focus();
        setTimeout(() => {
          resultsRegion.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } else {
      onClear();
      // Move focus to error summary
      if (errorSummary.current) {
        errorSummary.current.focus();
        setTimeout(() => {
          errorSummary.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      } else {
        scrollTo(topSection);
      }
    }
  };

  const birthYearOptions = getBirthYearOptions(year, 69, 18).map((y) => ({
    value: y,
    label: y + "",
  }));
  const getMonthOptions = (locale: string) => {
    return Array.from({ length: 12 }, (_, i) => {
      return { value: i + 1, label: getMonthName(i, locale) };
    });
  };

  return (
    <ValidationContext.Provider value={validation}>
      <div ref={topSection}></div>
      <Form
        ref={formRef}
        noValidate
        validated={validation.validated}
        onSubmit={handleSubmit}
        className="calculator-form"
        id={"elake-laskuri-form"}
      >
        <ValidationFeedback
          feedback={validation.validationFeedback}
          ref={errorSummary}
        />
        <p className="required-fields-info">
          <span className="required">*</span>&nbsp;
          {t("requiredFieldsInfo")}
        </p>
        <Row>
          <Col md={12}>
            <InputGroup
              hasValidation
              className={`${
                validation.validated ? "was-validated" : "was-not-validated"
              }`}
            >
              <LargeSelectInput<number>
                fieldName="elake-arvio-laskuri.birthYear"
                required={true}
                showValidation={validation.validated}
                label={
                  <span className="error-label">
                    {t("elake-arvio-laskuri.birthYear.label")}
                    <span className="required">*</span>
                  </span>
                }
                helpTextAccordion={
                  <HelpTextAccordion fieldName="elake-arvio-laskuri.birthYear" />
                }
                options={birthYearOptions}
                value={formModel.birthYear}
                setValue={(selected) => {
                  if (selected?.value !== undefined) {
                    setFormModel((fm) => ({
                      ...fm,
                      birthYear: selected.value,
                    }));
                  }
                }}
              />
              <LargeSelectInput<number>
                fieldName="elake-arvio-laskuri.birthMonth"
                required={true}
                showValidation={validation.validated}
                label={
                  <span className="error-label">
                    {t("elake-arvio-laskuri.birthMonth.label")}
                    <span className="required">*</span>
                  </span>
                }
                options={getMonthOptions(language)}
                value={formModel.birthMonth}
                setValue={(selected) => {
                  if (selected?.value !== undefined) {
                    setFormModel((fm) => ({
                      ...fm,
                      birthMonth: selected.value,
                    }));
                  }
                }}
              />
              <NumberInput
                fieldName={"elake-arvio-laskuri.income"}
                initialValue={formModel.income}
                min={0}
                max={999999999}
                onlyInts={true}
                required={true}
                onValidValueChange={(income?) => {
                  if (income !== undefined) {
                    setFormModel((fm) => ({ ...fm, income }));
                  }
                }}
              />
              <NumberInput
                fieldName={"elake-arvio-laskuri.accruedPension"}
                extraInformationField={medianAccruedPension}
                initialValue={formModel.accruedPension}
                min={0}
                max={999999999}
                onlyInts={true}
                required={true}
                onValidValueChange={(accruedPension?) => {
                  if (accruedPension !== undefined) {
                    setFormModel((fm) => ({ ...fm, accruedPension }));
                  }
                }}
              />
              <LargeSelectInput
                fieldName="elake-arvio-laskuri.incomeDevelopment"
                label={
                  <span className="error-label">
                    {t("elake-arvio-laskuri.incomeDevelopment.label")}
                  </span>
                }
                helpTextAccordion={
                  <HelpTextAccordion fieldName="elake-arvio-laskuri.incomeDevelopment" />
                }
                options={[
                  {
                    value: "projected",
                    label: t("elake-arvio-laskuri.incomeDevelopment.projected"),
                  },
                  {
                    value: "static",
                    label: t("elake-arvio-laskuri.incomeDevelopment.static"),
                  },
                ]}
                value={formModel.incomeDevelopment}
                setValue={(selected) => {
                  if (selected?.value) {
                    setFormModel((fm) => ({
                      ...fm,
                      incomeDevelopment: selected.value,
                    }));
                  }
                }}
              />
            </InputGroup>
          </Col>
        </Row>
        <Row>
          <Col sm={6} className="mt-2">
            <Button
              type="submit"
              aria-controls="results-region"
              onClick={() => {
                trackEvent("Button", t("elake-arvio-laskuri.calculate"));
              }}
            >
              {t("elake-arvio-laskuri.calculate")}
            </Button>
          </Col>
        </Row>
      </Form>
    </ValidationContext.Provider>
  );
};

export default ElakeArvioCalculator;
